import { refreshTokenApi } from "@/api/services/admin/auth";
import { ConfirmReloadModal } from "@/components/commons/ConfirmReloadModal";
import { LoadingScreen } from "@/components/elements/Loading/Loading";
import { useDialog } from "@/hooks/useDialog";
import { AdminRoutes } from "@/routes/config";
import { clientApi, isAuth, logout, setAxiosAuth } from "@/utils/api";
import { redirectToLogin } from "@/utils/center";
import { Typography } from "@mui/material";
import Cookies from "js-cookie";
import { FC, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

export const withAuth: <CP extends {}>(WrappedComponent: FC<CP>) => FC<CP> = (WrappedComponent) => (props) => {
  const { pathname } = useLocation();
  const [isVerified, setIsVerified] = useState(false);
  const [userId] = useState(Cookies.get("admin-user-id"));
  const navigate = useNavigate();
  const confirmReload = useDialog();

  useEffect(() => {
    // Every 2 seconds check user id, if user id has changed then redirect to login page
    const intervalId = setInterval(() => {
      if (userId && userId != Cookies.get("admin-user-id")) {
        redirectToLogin();
      }
    }, 2000);

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    const verifyAuth = async () => {
      // set axios auth at init
      if (isAuth() && !clientApi.defaults.headers.common.Authorization) {
        setAxiosAuth();
      }

      // verify admin routes
      if (!isAuth() && AdminRoutes.some((path) => pathname.startsWith(path))) {
        // Access token expired — attempt refresh before redirecting to login
        const email = Cookies.get("admin-email");
        const refreshToken = Cookies.get("admin-refresh-token");

        if (email && refreshToken) {
          try {
            await refreshTokenApi({ email, refreshToken });
            setAxiosAuth();
            setIsVerified(true);
            return;
          } catch {
            // Refresh failed — redirect to login
          }
        }

        logout();
        redirectToLogin();
        return;
      }

      setIsVerified(true);

      // check version build
      try {
        const response = await fetch("/version.json", {
          headers: { "Cache-Control": "no-cache" },
        });
        const { version } = await response.json();
        const currentVersion = localStorage.getItem("app-version");

        // show modal and update version
        if (version && (!currentVersion || version !== parseInt(currentVersion))) {
          localStorage.setItem("app-version", version);
          const agree = await confirmReload.show();
          if (agree) {
            window.location.reload();
          }
        }
      } catch (error) {
        console.error("Error checking version:", error);
      }
    };

    verifyAuth();
  }, [pathname, navigate]);

  if (!isVerified) return <LoadingScreen />;

  return (
    <>
      <ConfirmReloadModal open={confirmReload.open} onOk={confirmReload.confirm}>
        <Typography>システムがアップデートされました。最新の状態にするため、ページをリロードしてください。</Typography>
      </ConfirmReloadModal>
      <WrappedComponent {...props} />
    </>
  );
};
